import * as Yup from "yup";
import moment from "moment";
import lodash from "lodash";


export const FactoryAcceptanceTestServiceOrderPhysicalInspectionResults = ["Present", "Absent"] as const;
export type FactoryAcceptanceTestServiceOrderPhysicalInspectionResult = typeof FactoryAcceptanceTestServiceOrderPhysicalInspectionResults[number];

export const FactoryAcceptanceTestServiceOrderTestResults = ["Pass", "Fail"] as const;
export type FactoryAcceptanceTestServiceOrderTestResult = typeof FactoryAcceptanceTestServiceOrderTestResults[number];

export const FactoryAcceptanceTestServiceOrderFactoryAssessmentResults = [
  "Poor", "Average", "Good", "VeryGood", "Excellent"
] as const;
export type FactoryAcceptanceTestServiceOrderFactoryAssessmentResult = typeof FactoryAcceptanceTestServiceOrderFactoryAssessmentResults[number];

type PhysicalInspectionResult = {
  status: FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
  notes: string
}

type TestResult = {
  status: FactoryAcceptanceTestServiceOrderTestResult,
  reference: string
}

export interface IResolveFATServiceOrderFormSchema {
  testInfo: {
    fatDate: string;
    projectName: string;
    numberOfECGReps: number;
    numberOfSupplierReps: number;
    fatStartDate: string;
    fatToDate: string;
    areaOfDeployment: string;
    address: string;
    ghanaPostAddress: string;
    representatives: {
      fullName: string;
      phoneNumber: string;
      designation: string;
    }[],
  },
  productInfo: {
    type: string;
    description: string;
    samplingTechnique: string
    sampledQuantity: number;
    serialNumbers: string[];
    testEquipment: string;
    packaging: string;
  },
  productCharacteristics: {
    operatingVoltage: string;
    operatingCurrent: string;
    frequency: string;
    technology: string;
    energy: string; // kWh
    reactiveEnergy: string; // kVArh
    modeOfPayment: string;
    standards: string;
  },
  physicalInspection: {
    keypad: PhysicalInspectionResult;
    provisionForSealing: PhysicalInspectionResult
    display: PhysicalInspectionResult;
    terminalCover: PhysicalInspectionResult;
    provisionForMounting: PhysicalInspectionResult;
    connectionDiagram: PhysicalInspectionResult;
    communicationPort: PhysicalInspectionResult;
    userInterfaceUnit: PhysicalInspectionResult;
    ledIndicator: PhysicalInspectionResult;
    manufacturersNameAndTrademark: PhysicalInspectionResult;
    typeDesignation: PhysicalInspectionResult;
    numberOfPhasesAndWires: PhysicalInspectionResult;
    serialNumber: PhysicalInspectionResult;
    monthAndYearOfManufacture: PhysicalInspectionResult;
    referenceVoltage: PhysicalInspectionResult;
    classIndex: PhysicalInspectionResult;
    ecgPrintedOnMeter: PhysicalInspectionResult;
    batchNumberAndDate: PhysicalInspectionResult;
    terminals: PhysicalInspectionResult;
    tariff: PhysicalInspectionResult;
  },
  test: {
    metrologicalPerformance: TestResult;
    tamperTests: TestResult;
    functionalTests: TestResult;
    tariffChecks: TestResult;
    DLMSCommunication: TestResult;
    STSComplianceTest: TestResult;
    detailedTest: TestResult;
    eventsAndAlarms: TestResult;
    voltageVariation: TestResult;
  },
  factoryAssessment: {
    overallCondition: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    safetyMeasures: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    equipment: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    productionCapacity: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    utilities: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    complianceAndCertifications: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    environmentalImpact: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    workforce: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    security: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
    documentation: FactoryAcceptanceTestServiceOrderFactoryAssessmentResult;
  },
  extraInfo: {
    observations: string[];
    conclusion: string;
    recommendations: string;
    supportingImageUrls: string[];
    routineTestReports: string[];
  }
}

export const PhysicalInspectionHeaderMap: Record<keyof IResolveFATServiceOrderFormSchema['physicalInspection'], string> = {
  keypad: "Keypad",
  provisionForSealing: "Provision for Sealing",
  display: "Display",
  terminalCover: "Terminal Cover",
  provisionForMounting: "Provision for Mounting",
  connectionDiagram: "Connection Diagram",
  communicationPort: "Communication Port",
  userInterfaceUnit: "User Interface Unit",
  ledIndicator: "LED Indicator",
  manufacturersNameAndTrademark: "Manufacturer's Name and Trademark",
  typeDesignation: "Type Designation",
  numberOfPhasesAndWires: "Number of Phases and Wires",
  serialNumber: "Serial Number (Numeric and Barcode)",
  monthAndYearOfManufacture: "Month and Year of Manufacture",
  referenceVoltage: "Reference Voltage",
  classIndex: "Class Index of Meter",
  ecgPrintedOnMeter: `"Property of Electricity Company of Ghana" is printed on the meter`,
  batchNumberAndDate: "Batch Number and Date",
  terminals: "Terminals",
  tariff: "Tariff",
}

export const TestHeaderMap: Record<keyof IResolveFATServiceOrderFormSchema['test'], string> = {
  metrologicalPerformance: "Metrological Performance as per ECG’s requirements",
  tamperTests: "Tamper Tests as per ECG’s requirements",
  functionalTests: "Functional Tests as per ECG’s requirements",
  tariffChecks: "Tariff Checks as per ECG’s requirements",
  DLMSCommunication: "DLMS Communication - Compliance Test (IEC 62056-4-7)",
  STSComplianceTest: "STS Compliance Test (IEC 62055-31)",
  detailedTest: "Detailed Test for STS (IEC 62055-41)",
  eventsAndAlarms: "Events and Alarms as per ECG’s requirements",
  voltageVariation: "Voltage Variation as per ECG’s requirements",
}

export const FactoryAssessmentHeaderMap: Record<keyof IResolveFATServiceOrderFormSchema['factoryAssessment'], string> = {
  overallCondition: "Overall Condition",
  safetyMeasures: "Safety Measures",
  equipment: "Equipment",
  productionCapacity: "Production Capacity",
  utilities: "Utilities",
  complianceAndCertifications: "Compliance and Certifications",
  environmentalImpact: "Environmental Impact",
  workforce: "Workforce",
  security: "Security",
  documentation: "Documentation",
}

export const ResolveFactoryAcceptanceTestInitialValues = {
  testInfo: {
    fatDate: "",
    projectName:  "",
    numberOfECGReps:  0,
    numberOfSupplierReps:  0,
    fatStartDate:  "",
    fatToDate:  "",
    areaOfDeployment:  "",
    address:  "",
    ghanaPostAddress:  "",
    representatives: [] as {fullName: string, phoneNumber: string, designation: string}[],
  },
  productInfo: {
    type: "",
    description: "",
    samplingTechnique: "",
    sampledQuantity: 0,
    serialNumbers: [] as string[],
    testEquipment: "",
    packaging: "",
  },
  productCharacteristics: {
    operatingVoltage: "",
    operatingCurrent: "",
    frequency: "",
    technology: "",
    energy: "",
    reactiveEnergy: "",
    modeOfPayment: "",
    standards: "",
  },
  test: {
    metrologicalPerformance: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    tamperTests: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    functionalTests: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    tariffChecks: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    DLMSCommunication: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    STSComplianceTest: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    detailedTest: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    eventsAndAlarms: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
    voltageVariation: {
      status: "Fail" as FactoryAcceptanceTestServiceOrderTestResult,
      reference: ""
    },
  },
  physicalInspection: {
    keypad: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    provisionForSealing: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    display: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    terminalCover: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    provisionForMounting: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    connectionDiagram: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    communicationPort: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    userInterfaceUnit: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    ledIndicator: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    manufacturersNameAndTrademark: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    typeDesignation: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    numberOfPhasesAndWires: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    serialNumber: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    monthAndYearOfManufacture:{
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    referenceVoltage: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    classIndex: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    ecgPrintedOnMeter: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    batchNumberAndDate: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    terminals: {
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
    tariff:{
      status: "Absent" as FactoryAcceptanceTestServiceOrderPhysicalInspectionResult,
      notes: "",
    },
  },
  factoryAssessment: {
    overallCondition: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    safetyMeasures: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    equipment: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    productionCapacity: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,  
    utilities: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    complianceAndCertifications: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    environmentalImpact: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    workforce: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    security: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
    documentation: "Poor" as FactoryAcceptanceTestServiceOrderFactoryAssessmentResult,
  },    
  extraInfo: {
    observations: [] as string[],
    conclusion: "",
    recommendations: "",
    supportingImageUrls: [] as string[],
    routineTestReports: [] as string[],
  }
}


export const TestInfoFormSchema = Yup.object().shape({
  projectName: Yup.string().required("Required"),
  numberOfECGReps: Yup.string().required("Required"),
  numberOfSupplierReps: Yup.string().required("Required"),
  fatStartDate: Yup.string().required("Required").test({
    message: "Invalid Date",
    test: (val) => {
      if(!val) return false;
      return moment(val).isSameOrBefore(moment());
    }
  }),
  fatToDate: Yup.string().required("Required").test({
    message: "Invalid Date",
    test: (val, ctx) => {
      if(!val) return false;
      return moment(val).isSameOrAfter(moment(ctx.parent?.fatStartDate));
    }
  }),
  areaOfDeployment: Yup.string().required("Required"),
  address: Yup.string().required("Required"),
  ghanaPostAddress: Yup.string().required("Required"),
  representatives: Yup.array()
    .of(Yup.object({
      fullName: Yup.string().required("Required"),
      phoneNumber: Yup.string().required("Required"),
      designation: Yup.string().required("Required"),
    })).min(1, "Indicate at least one representative")
});

export const ProductInfoSchema = Yup.object({
  type: Yup.string().required("Required"),
  description: Yup.string(),
  sampledQuantity: Yup.number().min(1).required("Required"),
  samplingTechnique: Yup.string().required("Required"),
  serialNumbers: Yup.array().of(Yup.string().required("Required")).min(1, "Enter at least one Serial Number"),
  testEquipment: Yup.string().required("Required"),
  packaging: Yup.string().required("Required"),
})

export const ProductCharacteristicsSchema = Yup.object({
  operatingVoltage: Yup.string().required("Required"),
  operatingCurrent: Yup.string().required("Required"),
  frequency: Yup.string().required("Required"),
  technology: Yup.string().required("Required"),
  energy: Yup.string().required("Required"),
  reactiveEnergy: Yup.string().required("Required"),
  modeOfPayment: Yup.string().required("Required"),
  standards: Yup.string().required("Required"),
})

export const ExtraInfoSchema = Yup.object({
  observations: Yup.array().of(Yup.string().required("Required")).min(1, "Enter at least one observation"),
  conclusion: Yup.string().required("Required"),
  recommendations: Yup.string(),
  supportingImageUrls: Yup.array().of(Yup.string()),
  routineTestReports: Yup.array().of(Yup.string().required("Required")).min(1, "Enter at least one routine test report"),
})

export const PhysicalInspectionSchema = Yup.object(
  lodash.chain(PhysicalInspectionHeaderMap)
    .keys()
    .map(
      (key) => ([
        key,
        Yup.object({
          status: Yup.string().required("Required"),
          notes: Yup.string(),
        }),
      ])
    )
    .fromPairs()
    .value()
)


export const TestSchema = Yup.object(
  lodash.chain(TestHeaderMap)
    .keys()
    .map(
      (key) => ([
        key,
        Yup.object({
          reference: Yup.string().required("Required"),
          status: Yup.string().required("Required"),
        }),
      ])
    )
    .fromPairs()
    .value()
)


export const FactoryAssessmentSchema = Yup.object(
  lodash.chain(FactoryAssessmentHeaderMap)
    .keys()
    .map(
      (key) => ([
        key,
        Yup.string().required("Required"),
      ])
    )
    .fromPairs()
    .value()
)
